import { PANEL_WS_PROTOCOL, ROUTES } from '../api/routes';
import { loadSession } from '../auth/session';
import { WS_URL } from '../config/env';
import { parsePanelEvent, type PanelEvent } from './events';

/**
 * Canal de estado en vivo.
 *
 * Uno solo para todo el panel: la lista, la ficha de nodo y el árbol lateral
 * escuchan la misma conexión. Abrir un WebSocket por pantalla multiplicaría
 * conexiones contra el backend sin dar nada a cambio.
 */

export type ConnectionState = 'connecting' | 'open' | 'closed';

export type LiveClient = {
  subscribe: (listener: (event: PanelEvent) => void) => () => void;
  onStateChange: (listener: (state: ConnectionState) => void) => () => void;
  getState: () => ConnectionState;
  close: () => void;
};

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
const PING_INTERVAL_MS = 25_000;

class PanelLiveClient implements LiveClient {
  private socket: WebSocket | null = null;
  private state: ConnectionState = 'connecting';
  private attempt = 0;
  private closed = false;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly listeners = new Set<(event: PanelEvent) => void>();
  private readonly stateListeners = new Set<(state: ConnectionState) => void>();

  constructor() {
    this.connect();
  }

  subscribe(listener: (event: PanelEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onStateChange(listener: (state: ConnectionState) => void): () => void {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  getState(): ConnectionState {
    return this.state;
  }

  close(): void {
    this.closed = true;
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.socket?.close();
    this.socket = null;
    this.setState('closed');
  }

  private emit(event: PanelEvent) {
    for (const listener of this.listeners) listener(event);
  }

  private setState(state: ConnectionState) {
    if (this.state === state) return;
    this.state = state;
    for (const listener of this.stateListeners) listener(state);
  }

  private connect() {
    if (this.closed) return;

    const session = loadSession();
    if (!session) {
      this.setState('closed');
      return;
    }

    this.setState('connecting');
    const socket = new WebSocket(`${WS_URL}${ROUTES.events}`, PANEL_WS_PROTOCOL);
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
      // El token viaja en el primer mensaje, no en la query: una URL con
      // credenciales acaba en los logs de cualquier proxy por el que pase.
      socket.send(JSON.stringify({ type: 'auth', accessToken: session.accessToken }));
      socket.send(JSON.stringify({ type: 'subscribe' }));
      this.setState('open');
      this.pingTimer = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: 'ping' }));
      }, PING_INTERVAL_MS);
    };

    socket.onmessage = (message: MessageEvent<string>) => {
      let payload: unknown;
      try {
        payload = JSON.parse(message.data);
      } catch {
        return;
      }
      const event = parsePanelEvent(payload);
      if (event) this.emit(event);
    };

    socket.onclose = () => {
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = null;
      this.socket = null;
      this.setState('closed');
      this.scheduleReconnect();
    };

    socket.onerror = () => socket.close();
  }

  /** Backoff exponencial con tope y ruido: cien paneles no deben reconectar a la vez. */
  private scheduleReconnect() {
    if (this.closed) return;
    const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** this.attempt);
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => this.connect(), delay * (0.75 + Math.random() * 0.5));
  }
}

let client: PanelLiveClient | null = null;

/** Se crea al primer uso, ya con sesión: antes del login no hay a qué suscribirse. */
export const getLiveClient = (): LiveClient => {
  client ??= new PanelLiveClient();
  return client;
};

export const closeLiveClient = (): void => {
  client?.close();
  client = null;
};
